import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { SonarQubeConnection } from '@/types'
import { AlertTriangle, X } from 'lucide-react'
import { CONNECTOR_LABELS } from './connectorConstants'

interface SonarQubeDisableConfirmDialogProps {
  connection: SonarQubeConnection | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isDisabling: boolean
}

export function SonarQubeDisableConfirmDialog({
  connection,
  open,
  onOpenChange,
  onConfirm,
  isDisabling,
}: SonarQubeDisableConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[640px] gap-6 p-8 shadow-[0_0_12px_rgba(0,36,50,0.16)]">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold tracking-[0.015em] text-card-foreground">Disable {CONNECTOR_LABELS.SonarQube}?</DialogTitle>
          <DialogClose aria-label="Close" className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <p className="text-base font-normal leading-[150%] tracking-[0.015em] text-foreground">
          Disabling this connector will remove access to linked SonarQube analysis context for this project.
        </p>

        {connection && (
          <div
            className="bg-warning rounded-lg p-4 border border-warning-foreground"
            data-testid="affected-sonarqube-project-warning"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-warning-foreground shrink-0 mt-0.5" />
              <div>
                <p className="text-base font-normal leading-[150%] tracking-[0.015em] text-warning-foreground">
                  The following SonarQube project will be disconnected:
                </p>
                <ul className="mt-5 ml-4 space-y-2 list-disc list-inside">
                  <li className="text-base leading-[150%] tracking-[0.015em] text-warning-foreground" data-testid="affected-sonarqube-project">
                    <span className="font-bold">{connection.projectName || connection.projectKey}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDisabling}
            data-testid="disable-connector-button"
          >
            <X className="h-4 w-4 mr-1" />
            {isDisabling ? 'Disabling...' : 'Disable Connector'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
